import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { Edit } from "@mui/icons-material";
import { Box, IconButton, Tooltip } from "@mui/material";
import MarkdownModal from "datasets/components/MarkdownModal";
import useDataset from "datasets/hooks/useDataset";
import Dataset from "datasets/types/interfaces/dataset";
import remarkGfm from "remark-gfm";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

const DescriptionEditor = ({ dataset, canEdit }: { dataset: Dataset; canEdit: boolean }) => {
  const { mutate } = useDataset();

  const [descriptionError, setDescriptionError] = useState("");
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [showEditDescButton, setShowEditDescButton] = useState(false);
  const [updatingDescription, setUpdatingDescription] = useState(false);

  const updateDescription = async (description: string) => {
    setDescriptionError("");
    setUpdatingDescription(true);

    try {
      const resp = await fetch(`${VITE_CATALOG_API}/datasets/${dataset?.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ description }),
      });

      if (!resp.ok) {
        throw new Error("Unable to update dataset description");
      }

      const datasetResp: Dataset = await resp.json();

      trackEvent("dataset_description_updated", {
        dataset_id: datasetResp.id,
        dataset_description: datasetResp.description,
      });

      mutate();
      setDescriptionModalOpen(false);
    } catch (e: any) {
      console.error(e);
      rumClient?.recordError(e);
      setDescriptionError(descriptionError);
    }

    setUpdatingDescription(false);
  };

  return (
    <Box
      onMouseEnter={() => {
        if (canEdit) {
          setShowEditDescButton(true);
        }
      }}
      onMouseLeave={() => {
        if (canEdit) {
          setShowEditDescButton(false);
        }
      }}
      sx={{
        lineHeight: 1.625,
        p: 0.5,
        position: "relative",
        pr: canEdit ? 4 : undefined,
      }}
    >
      <Box
        className="prose prose-sm max-w-none"
        sx={{
          img: {
            width: "100%",
          },
          "> :first-child": {
            mt: 0,
            pt: 0,
          },
          "> :last-child": {
            mb: 0,
            pb: 0,
          },
          "&:empty": {
            "&::before": {
              content: '"Description"',
              opacity: 0.5,
            },
          },
        }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{dataset.description}</ReactMarkdown>
      </Box>
      {canEdit && (
        <>
          <Tooltip arrow title="Edit description" enterDelay={500}>
            <IconButton
              data-testid="edit-dataset-description-button"
              sx={{
                opacity: showEditDescButton ? 1 : 0,
                position: "absolute",
                right: 0,
                top: 4,
                transition: "opacity 150ms ease-in-out",
              }}
              onClick={() => setDescriptionModalOpen(true)}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          {descriptionModalOpen && (
            <MarkdownModal
              error={descriptionError}
              loading={updatingDescription}
              onClose={() => setDescriptionModalOpen(false)}
              onSubmit={updateDescription}
              open={descriptionModalOpen}
              title="Edit Description"
              value={dataset.description}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default DescriptionEditor;
